import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'
import { MTSubModuleLabelMap } from '@/multi-territory/mt-constants/mt-label-map'
import { MTSubModule } from '@/multi-territory/mt-types/mt-returns'
import { Box } from '@mui/material'
import { NavLink, useLocation } from 'react-router'

type MTKeyFilterProps = {
  subModules: MTSubModule[]
  /** Base route to build sub-module paths, e.g. "/mt/returns/define" */
  baseRoute: string
}

const capitalizeFallbackLabel = (name: string): string => {
  if (!name) return ''
  const words = name.split(' ')
  return [
    words[0].charAt(0).toUpperCase() + words[0].slice(1),
    ...words.slice(1),
  ].join(' ')
}

/**
 * MTKeyFilter renders a horizontal tab bar for the enabled sub-modules
 * of the currently active parent module (e.g. Define -> Service master | Service keywords | Rule setting).
 */
export function MTKeyFilter({ subModules, baseRoute }: MTKeyFilterProps) {
  const location = useLocation()

  const enabledSubModules = subModules.filter(sm => sm.enabled !== false)

  if (enabledSubModules.length === 0) return null

  return (
    <Box
      component="nav"
      aria-label={
        GLOBAL_CONSTANTS.MT_TAB_NAVIGATION_LABEL ?? 'Sub-module navigation'
      }
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        width: '100%',
        overflowX: 'auto',
        borderBottom: '1px solid var(--border-strong)',
        backgroundColor: 'var(--bg-layer-1)',
        px: 2,
        gap: 0,
        mt:'12px',
        height:'48px'
      }}
    >
      {enabledSubModules
        .sort((a, b) => a.order - b.order)
        .map(sm => {
          const route = `${baseRoute}/${sm.code}`
          const label =
            MTSubModuleLabelMap[sm.code] ?? sm.label ?? capitalizeFallbackLabel(sm.code)
          const isActive = location.pathname.startsWith(route)

          return (
            <NavLink
              key={sm.code}
              to={route}
              style={{ textDecoration: 'none', flexShrink: 0 }}
              aria-label={`Navigate to ${label}`}
             
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: 'var(--font-size-sm, 14px)',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  color: isActive
                    ? 'var(--forbrand-primary)'
                    : 'var(--forbrand-secondary)',
                  borderBottom: isActive
                    ? '4px solid var(--forbrand-color)'
                    : '4px solid transparent',
                  transition: 'color 0.2s, border-color 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                {label}
              </Box>
            </NavLink>
          )
        })}
    </Box>
  )
}

